import {getTestProps} from '@benchling/js-test-utils';
import {captureException} from '@sentry/browser';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState, useEffect, useRef, useMemo} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {CanvasRef} from 'canvas-core/chat/bnchCanvasMarkdown';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {AgentType, ChatType, LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {ChatContext} from 'llm/chat-library/ChatContext';
import {getChatFileDownloadUrl} from 'llm/chat-library/ChatDAO';
import {
  AIWarning,
  ChatInput,
  useChatInputControls,
  useVoiceStartLifecycleGuard,
} from 'llm/chat-library/ChatInput';
import {ChatMessageThread} from 'llm/chat-library/ChatMessageThread';
import ChatScrollContainer from 'llm/chat-library/ChatScrollContainer';
import ComposeOverEntryBanner from 'llm/chat-library/ComposeOverEntryBanner';
import {getAgentWorkspaceFolderId} from 'llm/chat-library/getAgentWorkspaceFolderId';
import {getSelfScheduledState} from 'llm/chat-library/getSelfScheduledState';
import {getTargetItem} from 'llm/chat-library/getTargetItem';
import {getChatLayout, isRightSidebarLayout} from 'llm/chat-library/layouts/ChatLayoutRegistry';
import {useAutoOpenNewArtifacts} from 'llm/chat-library/layouts/right-sidebar/useAutoOpenNewArtifacts';
import LegacyAgentDeprecationBanner from 'llm/chat-library/LegacyAgentDeprecationBanner';
import SelfScheduledBanner from 'llm/chat-library/SelfScheduledBanner';
import {openSidebarItemDetail} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {
  doesAgentTypeSupportInterrupts,
  doesAssistantDeprecateAgent,
  doesChatTypeShowProgress,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {useArtifacts} from 'llm/chat-library/useArtifacts';
import {getActiveCanvasDefinitionId} from 'llm/chat-library/useCanvasArtifact';
import {useMessageThread} from 'llm/chat-library/useMessageThread';
import {LatestUserMessageInfo} from 'llm/ChatScrollTypes';
import {DraftIndicatorButton} from 'llm/data-capture/DraftIndicatorButton';
import {ProcedureToolMappingProvider} from 'llm/procedure-tool-library/ProcedureToolMappingContext';
import {usePendingVoiceToolInvocationCount} from 'llm/realtime-voice/PendingVoiceToolInvocations';
import {usePendingVoiceTranscripts} from 'llm/realtime-voice/PendingVoiceTranscripts';
import useRealtimeVoiceSession, {RealtimeVoiceSessionState} from 'llm/realtime-voice/useRealtimeVoiceSession';
import useVoiceUIController from 'llm/realtime-voice/VoiceUIController';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useIsPhoneWidth from 'ui-library/hooks/useIsPhoneWidth';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as modals from 'util/Modals';

export interface ChatProps {
  chatSessionId: string;
  chatType: ChatType;
  isExpanded: boolean;
  locked?: boolean;
}

/**
 * Generic chat component that supports multiple chat types (Deep Research, Compose, etc.).
 *
 * This component uses the artifact abstraction to manage outputs like draft documents,
 * and can show progress UI for chat types that support tool calls.
 */
export function Chat({chatSessionId, chatType, isExpanded, locked = false}: ChatProps): JSX.Element {
  const controls = useChatInputControls(chatSessionId);
  const {agentType, agentTypeError, isPending} = controls;
  const handleDraftDocumentCreatedRef = useRef<() => void>(() => {});
  const realtimeVoiceSessionControls = useRealtimeVoiceSession(
    chatSessionId,
    agentType ?? AgentType.ASSISTANT,
    {
      onDraftDocumentCreated: () => {
        handleDraftDocumentCreatedRef.current();
      },
    }
  );
  const voiceUIController = useVoiceUIController(realtimeVoiceSessionControls);
  const voiceStartLifecycleGuard = useVoiceStartLifecycleGuard({
    kind: 'chat',
    chatSessionId,
    startVoiceSession: voiceUIController.start,
  });
  const {sessionState: voiceSessionState, stopVoiceSession} = realtimeVoiceSessionControls;
  const [hasTrackedChatOpen, setHasTrackedChatOpen] = useState<boolean>(false);
  const [waitingForArtifactBeforeSave, setWaitingForArtifactBeforeSave] = useState<boolean>(false);

  // Persist scroll position across sidepanel expand/collapse remounts of ChatScrollContainer.
  // -1 means no saved position; 0+ is a valid scrollTop.
  const savedScrollPositionRef = useRef<number>(-1);

  // Treat pending + agentTypeError as a failed submit state rather than active submission.
  const isSubmitting = waitingForArtifactBeforeSave || (isPending && agentTypeError == null);

  const {llmEnableRightSidebar} = useRolloutFlags();
  // The rollout flag alone doesn't mean the right sidebar is mounted: ANALYSIS and LEGACY_DATA_IMPORT
  // keep their own layouts even when the flag is on.
  const usesRightSidebar = isRightSidebarLayout(chatType, {rightSidebarEnabled: llmEnableRightSidebar});

  const {
    events,
    isLoading,
    errorMessage,
    isThreadError,
    isAssistantRunning,
    isLastMessageFromUser,
    isSubmitErrorMessage,
    sendMessage,
    canInterruptAssistant,
    isInterrupting,
    interruptAssistant,
    progressData,
    citationData,
    latestApplicationState,
    sidebarData,
    fullAccessEnabled,
    grantFullAccess,
    revokeFullAccess,
    updateAgentWorkspaceFolder,
    chatSessionDisplayName,
    hasChatScopedWriteApproval,
  } = useMessageThread({
    chatSessionId,
    includeToolCalls: doesChatTypeShowProgress(chatType),
    disableRealtimeUpdates: locked,
    includeSidebarData: usesRightSidebar,
  });
  useEffect(() => {
    if ((locked || errorMessage != null) && voiceSessionState !== RealtimeVoiceSessionState.NOT_CONNECTED) {
      stopVoiceSession().catch(captureException);
    }
  }, [errorMessage, locked, stopVoiceSession, voiceSessionState]);
  const [latestUserMessageInfo, setLatestUserMessageInfo] = useState<LatestUserMessageInfo | null>(null);
  const pendingVoiceTranscripts = usePendingVoiceTranscripts(chatSessionId);
  const pendingVoiceToolCount = usePendingVoiceToolInvocationCount(chatSessionId);
  const isVoiceToolProcessing = pendingVoiceToolCount > 0;
  const eventsWithPendingVoiceTranscripts = useMemo(() => {
    const persistedEventIds = new Set(events.flatMap((event) => ('id' in event ? [event.id] : [])));
    const pendingEvents = pendingVoiceTranscripts
      .filter(({persistedEventId}) => persistedEventId == null || !persistedEventIds.has(persistedEventId))
      .map(({itemId, previousItemId, persistedEventId, role}) => ({
        __typename: 'ClientPendingVoiceTranscript' as const,
        itemId,
        previousItemId,
        persistedEventId,
        role: role === 'user' ? LLMRole.USER : LLMRole.ASSISTANT,
      }));
    return [...events, ...pendingEvents];
  }, [events, pendingVoiceTranscripts]);

  const showStopButton =
    ((canInterruptAssistant && isAssistantRunning) || isInterrupting) &&
    !isSubmitErrorMessage &&
    agentType != null &&
    doesAgentTypeSupportInterrupts(agentType);

  const handleInterrupt = useCallback(async () => {
    if (!showStopButton || isInterrupting) {
      return;
    }
    await interruptAssistant();
  }, [interruptAssistant, isInterrupting, showStopButton]);

  const isPhoneWidth = useIsPhoneWidth();

  // Use the artifacts hook - handles document and source file artifacts from application state
  const artifacts = useArtifacts({
    latestApplicationState,
    isExpanded,
    chatType,
    chatSessionId,
    usesRightSidebar,
  });
  const {
    primaryArtifact,
    canvasArtifact,
    isPhoneArtifactOpen,
    isPhoneCanvasOpen,
    openArtifact,
    closePhoneArtifact,
    openCanvas,
    closePhoneCanvas,
  } = artifacts;

  // The sidebar covers the whole panel in the collapsed desktop view and on phone (see below).
  const isRightSidebarFullWidth = isPhoneWidth || !isExpanded;
  const {openVoiceCreatedDraft} = useAutoOpenNewArtifacts({
    enabled: usesRightSidebar,
    isLoading,
    documentId: primaryArtifact?.data.document_api_identifier ?? null,
    canvasId: getActiveCanvasDefinitionId(latestApplicationState),
    fullWidth: isRightSidebarFullWidth,
  });

  const handleDraftDocumentCreated = useCallback(() => {
    if (usesRightSidebar) {
      openVoiceCreatedDraft();
      return;
    }
    if (isPhoneWidth) {
      openArtifact();
      return;
    }
    if (!isExpanded) {
      SidepanelActions.expand();
    }
  }, [isExpanded, isPhoneWidth, usesRightSidebar, openArtifact, openVoiceCreatedDraft]);
  handleDraftDocumentCreatedRef.current = handleDraftDocumentCreated;

  // The draft-document indicator in the chat thread opens the document. With the right sidebar it
  // opens that artifact's detail view directly; otherwise it falls back to the legacy open behavior.
  const handleOpenDraftDocument = useCallback(() => {
    const documentId = primaryArtifact?.data.document_api_identifier;
    if (usesRightSidebar && documentId != null) {
      openSidebarItemDetail(documentId);
      return;
    }
    openArtifact();
  }, [usesRightSidebar, openArtifact, primaryArtifact]);

  // A canvas card in the chat thread opens the canvas. With the right sidebar, make it the canvas
  // artifact and open its detail view directly; the sidebar resolves that view from the canvas
  // artifact even when the backend doesn't list the canvas (see RightSidebarLayout). Otherwise fall
  // back to the legacy open behavior (phone overlay or expand).
  const handleOpenCanvas = useCallback(
    (canvas: CanvasRef) => {
      openCanvas(canvas);
      if (usesRightSidebar) {
        // Canvas detail views are keyed by canvasDefinitionId, the backend externalId (see useSidebarItems).
        openSidebarItemDetail(canvas.canvasDefinitionId);
      }
    },
    [openCanvas, usesRightSidebar]
  );

  const handleOpenFile = useCallback(
    (sessionId: string, externalFileId: string, _filename: string, _mimeType: string) => {
      window.open(getChatFileDownloadUrl(sessionId, externalFileId), '_blank', 'noopener,noreferrer');
    },
    []
  );

  const chatContextValue = useMemo(
    () => ({
      openCanvas: handleOpenCanvas,
      openFile: handleOpenFile,
    }),
    [handleOpenCanvas, handleOpenFile]
  );

  const toolCalls = useMemo(() => citationData?.toolCalls ?? [], [citationData]);

  const targetItem = getTargetItem(agentType, latestApplicationState);
  const selfScheduledState = getSelfScheduledState(latestApplicationState);
  // Scoped to co-scientist: this drives the co-scientist-only workspace folder chip/picker, not the
  // "Full access" chip tooltip below, which should name the folder for any agent type (background
  // agents/routines can create folder-scoped chats outside co-scientist too).
  const agentWorkspaceFolderId =
    agentType === AgentType.CO_SCIENTIST ? getAgentWorkspaceFolderId(latestApplicationState) : null;
  const fullAccessFolderId = getAgentWorkspaceFolderId(latestApplicationState);
  const hasAgentTypeError = agentTypeError != null;

  // Track opening of AI chat (only once per session)
  useEffect(() => {
    if (!isLoading && !hasTrackedChatOpen) {
      // TODO(BNCH-133493): openedNewChat is always false - also this should be invoked for all chat types.
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.OPENED_LLM_CHAT, {
        chatType,
        messageCount: events.filter((event) => event.__typename === 'DisplayMessageChatEventNode').length,
        openedNewChat: Boolean(!chatSessionId),
      });
      setHasTrackedChatOpen(true);
    }
  }, [chatSessionId, chatType, isLoading, hasTrackedChatOpen, events]);

  const handleSubmitMessage = useCallback(async () => {
    if (isVoiceToolProcessing) {
      return;
    }
    if (!agentType) {
      // This is unexpected, but just in case, notify the user why their message was not sent.
      notifications.error('An agent type must be selected before sending a message', {
        id: 'chat-agent-type-required',
      });
      return;
    }

    // If we need to retry, show a confirmation modal
    if (isLastMessageFromUser) {
      const confirmed = await modals.confirm(
        'Alert',
        'The previous message encountered an error and will be removed. Do you want to continue?',
        ButtonStyle.PRIMARY,
        {
          acceptText: 'Continue',
          rejectText: 'Cancel',
          promiseCompatible: true,
          enableRedesign: true,
        }
      );

      if (!confirmed) {
        return;
      }
    }

    // Wait for artifact saves before sending
    if (primaryArtifact?.beforeSendMessage) {
      setWaitingForArtifactBeforeSave(true);
      try {
        await primaryArtifact.beforeSendMessage();
      } finally {
        setWaitingForArtifactBeforeSave(false);
      }
    }

    // Draft state already has everything - just submit
    sendMessage({retry: isLastMessageFromUser});
  }, [primaryArtifact, agentType, sendMessage, isLastMessageFromUser, isVoiceToolProcessing]);

  // If we have an error message, we may or may not have any past chat history to show. If there is
  // no past chat history (e.g. error on initial pageload), just show an error for the whole chat.
  // Otherwise, we will render the events we have below with a staleness message.
  if (errorMessage != null && events.length === 0) {
    return (
      <div
        className={css(styles.errorBannerContainer)}
        {...getTestProps({component: 'Chat', element: 'error-banner-container'})}
      >
        <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />
      </div>
    );
  }

  const showLegacyAgentDeprecationBanner =
    BenchlingConfig.LLM_SHOW_LEGACY_DEPRECATION_BANNER &&
    agentType != null &&
    doesAssistantDeprecateAgent(agentType);

  const isComposeOverEntry =
    agentType ===
      (BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT ? AgentType.ASSISTANT : AgentType.DATA_CAPTURE) &&
    targetItem != null;

  const messageThreadElement =
    // events may be non-empty while loading due to optimistic messages
    events.length === 0 && (isLoading || isAssistantRunning) ? (
      <CenteredHorizontalThrobber {...getTestProps({component: 'Chat', element: 'loading-throbber'})} />
    ) : (
      <ChatMessageThread
        events={eventsWithPendingVoiceTranscripts}
        chatSessionId={chatSessionId}
        chatSessionDisplayName={chatSessionDisplayName}
        onUpdateLatestUserMessageInfo={setLatestUserMessageInfo}
        banner={
          isComposeOverEntry && !BenchlingConfig.LLM_AGENT_APPROVALS_RELEASED ? (
            <ComposeOverEntryBanner />
          ) : null
        }
        actionButton={
          primaryArtifact ? (
            <DraftIndicatorButton
              onClick={handleOpenDraftDocument}
              artifactType={primaryArtifact.artifactType}
            />
          ) : null
        }
        readonly={locked}
        progressData={progressData}
        citationData={citationData}
        hasChatScopedWriteApproval={hasChatScopedWriteApproval}
      />
    );

  const chatContent = (
    <ChatScrollContainer
      chatMessagesElement={messageThreadElement}
      chatInputElement={
        locked ? null : (
          <>
            <ChatInput
              chatSessionId={chatSessionId}
              voiceUIController={voiceUIController}
              pendingVoiceToolCount={pendingVoiceToolCount}
              {...controls}
              onSubmit={handleSubmitMessage}
              showStopButton={showStopButton}
              onInterrupt={handleInterrupt}
              isInterrupting={isInterrupting}
              inputDisabled={
                isVoiceToolProcessing ||
                (isAssistantRunning && !isSubmitErrorMessage) ||
                (isSubmitting && !isSubmitErrorMessage) ||
                (errorMessage != null && !hasAgentTypeError && !isThreadError)
              }
              allowedChatTypes={[chatType]}
              agentWorkspaceFolderId={agentWorkspaceFolderId}
              fullAccessFolderId={fullAccessFolderId}
              onSetAgentWorkspaceFolder={updateAgentWorkspaceFolder}
              targetItem={targetItem}
              fullAccessEnabled={fullAccessEnabled}
              writeActionsEnabled={isLoading ? undefined : hasChatScopedWriteApproval}
              onGrantFullAccess={grantFullAccess}
              onRevokeFullAccess={revokeFullAccess}
              voiceStartLifecycleGuard={voiceStartLifecycleGuard}
            />
            <AIWarning />
          </>
        )
      }
      latestUserMessageInfo={latestUserMessageInfo}
      autoScroll={true}
      savedScrollPositionRef={savedScrollPositionRef}
    />
  );

  const isEditable =
    (!isAssistantRunning || isSubmitErrorMessage) && !locked && (!isSubmitting || isSubmitErrorMessage);

  // Get the appropriate layout for this chat type
  const LayoutComponent = getChatLayout(chatType, {rightSidebarEnabled: llmEnableRightSidebar});

  let content;
  if (isPhoneWidth && usesRightSidebar) {
    // Phone with the right sidebar: render the layout full-width, the same mode as the collapsed
    // desktop view. The sidebar covers the whole panel and shows the selected artifact; the chat
    // shows when it is closed. This replaces the legacy full-screen artifact/canvas overlay below.
    content = (
      <LayoutComponent
        chatContent={chatContent}
        artifacts={artifacts}
        sidebarData={sidebarData}
        isEditable={isEditable}
        fullWidth={true}
      />
    );
  } else if (isPhoneWidth) {
    // Mobile: show chat by default, or artifact when the user has opened it.
    if (isPhoneCanvasOpen && canvasArtifact) {
      const CanvasComponent = canvasArtifact.Component;
      content = (
        <CanvasComponent data={canvasArtifact.data} editable={isEditable} onClose={closePhoneCanvas} />
      );
    } else if (isPhoneArtifactOpen && primaryArtifact) {
      const ArtifactComponent = primaryArtifact.Component;
      // TODO: ideally the component with the close is wrapping the artifact, i.e.
      // not a concern of a given artifact component.
      content = (
        <ArtifactComponent data={primaryArtifact.data} editable={isEditable} onClose={closePhoneArtifact} />
      );
    } else {
      content = chatContent;
    }
  } else if (isExpanded) {
    // Desktop expanded view: use layout component (handles artifacts internally)
    content = (
      <LayoutComponent
        chatContent={chatContent}
        artifacts={artifacts}
        sidebarData={sidebarData}
        isEditable={isEditable}
      />
    );
  } else if (usesRightSidebar) {
    content = (
      <LayoutComponent
        chatContent={chatContent}
        artifacts={artifacts}
        sidebarData={sidebarData}
        isEditable={isEditable}
        fullWidth={true}
      />
    );
  } else {
    // Desktop collapsed view: show chat only
    content = chatContent;
  }

  return (
    <div className={css(styles.contentContainer)}>
      {showLegacyAgentDeprecationBanner && (
        <div className={css(styles.infoBannerContainer)}>
          <LegacyAgentDeprecationBanner />
        </div>
      )}
      {selfScheduledState != null && !isAssistantRunning && (
        <div className={css(styles.infoBannerContainer)}>
          <SelfScheduledBanner selfScheduledState={selfScheduledState} />
        </div>
      )}
      {errorMessage != null && !isThreadError && !isSubmitting && !isAssistantRunning && (
        <div
          className={css(styles.errorBannerContainer)}
          {...getTestProps({component: 'Chat', element: 'error-banner-with-chat'})}
        >
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text={`${errorMessage} Showing previously-loaded chat.`}
          />
        </div>
      )}
      <ProcedureToolMappingProvider toolCalls={toolCalls}>
        <ChatContext.Provider value={chatContextValue}>{content}</ChatContext.Provider>
      </ProcedureToolMappingProvider>
    </div>
  );
}

const styles = StyleSheet.create({
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minHeight: 0,
    flex: 1,
  },
  errorBannerContainer: {
    margin: '16px 16px 0',
  },
  infoBannerContainer: {
    margin: '16px 16px 0',
  },
});
